import { useEffect, useState } from "react";
import { createSpace, deleteSpace, joinSpace, leaveSpace, listItems, listMembers, listSpaces, regenCode, getActivity, getComments, addComment, deleteComment } from "./SpacesService";
import { useToast } from "../ToastContext";
import { useApiCall } from "../useApiCall";
import "./SpacesPage.css";

export default function SpacesPage() {
  const toast = useToast();
  const { execute } = useApiCall();
  const [spaces, setSpaces] = useState([]);
  const [view, setView] = useState("list"); // list | create | join | detail
  const [activeSpace, setActiveSpace] = useState(null);
  const [members, setMembers] = useState([]);
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [joinCode, setJoinCode] = useState("");

  useEffect(() => { loadSpaces(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSpaces = async () => {
    const data = await execute(listSpaces);
    if (data) setSpaces(data);
  };

  const openDetail = async (space) => {
    setActiveSpace(space);
    setSelectedItemId(null);
    setComments([]);
    const [m, it, act] = await Promise.all([listMembers(space.id), listItems(space.id), getActivity(space.id)]);
    setMembers(m);
    setItems(it);
    setActivity(act);
    setView("detail");
  };

  const openItemComments = async (item) => {
    setSelectedItemId(item.id);
    const cmts = await getComments(activeSpace.id, item.id);
    setComments(cmts);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selectedItemId) return;
    const cmt = await addComment(activeSpace.id, selectedItemId, commentText.trim());
    if (cmt?.id) {
      setComments(prev => [...prev, cmt]);
      setCommentText("");
      toast("Comment added", "success");
      // refresh activity
      getActivity(activeSpace.id).then(setActivity);
    }
  };

  const handleDeleteComment = async (commentId) => {
    await deleteComment(commentId);
    setComments(prev => prev.filter(c => c.id !== commentId));
    toast("Comment removed", "info");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    const space = await execute(() => createSpace(createName.trim(), createDesc.trim()));
    if (space?.id) {
      setSpaces(prev => [space, ...prev]);
      toast("Space created! Share the invite code with your household.", "success");
      setCreateName(""); setCreateDesc("");
      setView("list");
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    const space = await execute(() => joinSpace(joinCode.trim().toUpperCase()));
    if (space?.id) {
      setSpaces(prev => prev.find(s => s.id === space.id) ? prev : [space, ...prev]);
      toast("Joined space: " + space.name, "success");
      setJoinCode(""); setView("list");
    }
  };

  const handleLeave = async (id) => {
    if (!window.confirm("Leave this space?")) return;
    await execute(() => leaveSpace(id));
    setSpaces(prev => prev.filter(s => s.id !== id));
    toast("Left the space", "info");
    setView("list");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this space and remove all members?")) return;
    await execute(() => deleteSpace(id));
    setSpaces(prev => prev.filter(s => s.id !== id));
    toast("Space deleted", "info");
    setView("list");
  };

  const handleRegen = async (id) => {
    const data = await execute(() => regenCode(id));
    if (data?.inviteCode) {
      setActiveSpace(prev => ({ ...prev, inviteCode: data.inviteCode }));
      toast("New invite code generated", "success");
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-header">
        <h2>Shared Spaces</h2>
        <div className="sp-header-actions">
          <button className="sp-btn-primary" onClick={() => setView("create")}>+ Create Space</button>
          <button className="sp-btn-outline" onClick={() => setView("join")}>Join via Code</button>
        </div>
      </div>

      {view === "create" && (
        <form className="sp-form" onSubmit={handleCreate}>
          <h3>Create a Space</h3>
          <label>Name *<input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. Our Household" required /></label>
          <label>Description<textarea value={createDesc} onChange={e => setCreateDesc(e.target.value)} rows={2} placeholder="What this space is for…" /></label>
          <div className="sp-form-actions">
            <button type="submit" className="sp-btn-primary">Create</button>
            <button type="button" className="sp-btn-outline" onClick={() => setView("list")}>Cancel</button>
          </div>
        </form>
      )}

      {view === "join" && (
        <form className="sp-form" onSubmit={handleJoin}>
          <h3>Join a Space</h3>
          <label>Invite Code *
            <input value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="8-character code" maxLength={8} required />
          </label>
          <div className="sp-form-actions">
            <button type="submit" className="sp-btn-primary">Join</button>
            <button type="button" className="sp-btn-outline" onClick={() => setView("list")}>Cancel</button>
          </div>
        </form>
      )}

      {view === "list" && (
        <>
          {spaces.length === 0 && (
            <div className="sp-empty">No spaces yet. Create one or join with an invite code.</div>
          )}
          <div className="sp-grid">
            {spaces.map(s => (
              <div key={s.id} className="sp-card" onClick={() => openDetail(s)}>
                <div className="sp-card-name">{s.name}</div>
                {s.description && <div className="sp-card-desc">{s.description}</div>}
                <div className="sp-card-code">Code: <strong>{s.inviteCode}</strong></div>
              </div>
            ))}
          </div>
        </>
      )}

      {view === "detail" && activeSpace && (
        <div className="sp-detail">
          <button className="sp-back" onClick={() => setView("list")}>← Back</button>
          <h3>{activeSpace.name}</h3>
          {activeSpace.description && <p className="sp-detail-desc">{activeSpace.description}</p>}

          <div className="sp-invite-row">
            <span className="sp-invite-label">Invite code:</span>
            <code className="sp-invite-code">{activeSpace.inviteCode}</code>
            <button className="sp-btn-xs" onClick={() => { navigator.clipboard?.writeText(activeSpace.inviteCode); toast("Copied!", "success"); }}>Copy</button>
            <button className="sp-btn-xs" onClick={() => handleRegen(activeSpace.id)}>Regenerate</button>
          </div>

          <h4>Members ({members.length})</h4>
          <div className="sp-members">
            {members.map(m => (
              <div key={m.id} className="sp-member-chip">
                {m.userId} <span className="sp-member-role">{m.role}</span>
              </div>
            ))}
          </div>

          <h4>Shared Planner Items ({items.length})</h4>
          {items.length === 0
            ? <p className="sp-empty-small">No items tagged to this space yet. Set a planner item's space to this space ID to share it.</p>
            : (
              <div className="sp-items-list">
                {items.map(i => (
                  <div key={i.id} className="sp-item-row">
                    <div className="sp-item-info">
                      <span className="sp-item-title">{i.title}</span>
                      <span className="sp-item-status">[{i.status}]</span>
                    </div>
                    <button className="sp-btn-xs"
                      onClick={() => openItemComments(i) === selectedItemId ? setSelectedItemId(null) : openItemComments(i)}>
                      💬 Comments
                    </button>
                  </div>
                ))}
              </div>
            )
          }

          {/* Comments panel for selected item */}
          {selectedItemId && (
            <div className="sp-comments-panel">
              <div className="sp-comments-title">
                Comments — {items.find(i => i.id === selectedItemId)?.title || selectedItemId}
                <button className="sp-comments-close" onClick={() => setSelectedItemId(null)}>×</button>
              </div>
              <div className="sp-comments-list">
                {comments.length === 0 && <p className="sp-empty-small">No comments yet.</p>}
                {comments.map(c => (
                  <div key={c.id} className="sp-comment">
                    <div className="sp-comment-header">
                      <span className="sp-comment-author">{c.authorUsername || c.authorUserId}</span>
                      <span className="sp-comment-date">{c.createdAt?.replace("T"," ").slice(0,16)}</span>
                      <button className="sp-comment-del" onClick={() => handleDeleteComment(c.id)}>×</button>
                    </div>
                    <div className="sp-comment-body">{c.content}</div>
                  </div>
                ))}
              </div>
              <form className="sp-comment-form" onSubmit={handleAddComment}>
                <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add a comment…" className="sp-comment-input" />
                <button type="submit" className="sp-btn-primary" disabled={!commentText.trim()}>Post</button>
              </form>
            </div>
          )}

          {/* Activity feed */}
          {activity.length > 0 && (
            <div className="sp-activity">
              <h4>Recent Activity</h4>
              <div className="sp-activity-list">
                {activity.slice(0, 10).map(a => (
                  <div key={a.id} className="sp-activity-item">
                    <span className="sp-activity-msg">{a.message}</span>
                    <span className="sp-activity-date">{a.createdAt?.replace("T"," ").slice(0,16)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sp-detail-actions">
            <button className="sp-btn-outline" onClick={() => handleLeave(activeSpace.id)}>Leave Space</button>
            <button className="sp-btn-danger" onClick={() => handleDelete(activeSpace.id)}>Delete Space</button>
          </div>
        </div>
      )}
    </div>
  );
}
